// Third party
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
// Material
import { DataGridProProps, GridColDef } from "@mui/x-data-grid-pro";
import { useGridApiRef } from "@mui/x-data-grid-premium";
// Types
import { IRow } from "@/DataPages/types/common";
import { IDialogColumnTableProps, IVehicleDMSProperty } from "@/DataPages/types/inventory/models/inventory";
import { Box } from "@mui/material";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import { accountFormat } from "@/Common/helpers/number";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

const parseValue = (row: IVehicleDMSProperty): string => {
	switch (row.dataType) {
		case "boolean":
			return row.value === "true" ? "Yes" : "No";
		case "dollar":
			return `$${accountFormat(row.value)}`;
		case "date":
			return moment(row.value as string).format(DATE_FORMAT.usDateSlash);
		case "float":
			return parseFloat(row.value as string).toFixed(row.precision ?? 2);
		default:
			return row.value as string;
	}
};
const generateTreeData = (rows: IVehicleDMSProperty[], path: string[] = []) => {
	if (!rows.length) {
		return [];
	}
	const rowsMapped: IRow[] = [];
	for (const row of rows) {
		const newId = uuidv4();
		const labelValue =
			row.dataType === "list" || row.dataType === "object" ? row.displayName : `${row.displayName}: ${parseValue(row)}`;
		const newRow: IRow = {
			_id: newId,
			label: labelValue,
			path: [...path, newId],
		};
		rowsMapped.push(newRow);
		if (row.dataType === "list") {
			const children = generateTreeData(row.value as IVehicleDMSProperty[], newRow.path);
			rowsMapped.push(...children);
		}
		if (row.dataType === "object") {
			const children = generateTreeData(row.value as IVehicleDMSProperty[], newRow.path);
			rowsMapped.push(...children);
		}
	}
	return rowsMapped;
};

export default function DetailedColumnTable(props: Readonly<IDialogColumnTableProps>) {
	const apiRef = useGridApiRef();
	const pixelsPerChar = 23;
	const rows = generateTreeData(props.rows);
	const maxPathLength = rows.reduce((acc, current) => {
		return current.path.length > acc ? current.path.length : acc;
	}, 0);
	const columns: GridColDef[] = [
		{
			field: "_id",
			headerName: "ID",
			width: 10,
			filterable: false,
		},
		{
			field: "label",
			headerName: "",
			width: 150,
			renderCell: (params) => {
				const nestedLevel = maxPathLength - params.row.path.length;
				const paddingLeft = nestedLevel * pixelsPerChar;

				return (
					<Box sx={{ position: "fixed", width: "150px", marginLeft: `-${paddingLeft}px`, marginTop: "-10px" }}>
						<CustomCell value={params.value} hasTooltip maxWith={params.colDef.width ?? 0} fontSize="12px" />
					</Box>
				);
			},
		},
	];
	const getTreeDataPath: DataGridProProps["getTreeDataPath"] = (row) => row.path;
	const groupingColDef = {
		headerName: "",
		minWidth: maxPathLength * pixelsPerChar,
		width: maxPathLength * pixelsPerChar,
		hideDescendantCount: true,
		valueFormatter: () => "",
	};

	return (
		<DataSmartTable
			treeData
			getTreeDataPath={getTreeDataPath}
			groupingColDef={groupingColDef}
			apiRef={apiRef}
			columns={columns}
			rows={rows}
			headerHeight={0}
			defaultGroupingExpansionDepth={props.expandedRows ? -1 : 0}
			rowHeight={25}
			disableColumnMenu
			disableColumnSelector
			disableColumnResize
			disableColumnReorder
			disableColumnSorting
			columnVisibilityModel={{ _id: false }}
			customProps={{
				tableClassName: {
					"functional-table footer-table-included": true,
					"[&_.MuiDataGrid-treeDataGroupingCellToggle>button]:!w-[24px]": true,
					"[&_.MuiDataGrid-treeDataGroupingCellToggle>button]:!h-[24px]": true,
					"[&_.MuiDataGrid-main]:!h-auto": true,
					"overflow-hidden": true,
				},
				aggregationFooterClassName: "aggregation-footer regular-footer",
				expandableRows: false,
				hide: false,
			}}
		/>
	);
}
